/* eslint-disable @typescript-eslint/quotes */
import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, useTheme } from '@material-ui/core';
import dynamic from 'next/dynamic';

import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'next-i18next';
import { CompanyInfoType } from 'typings/markets/api';
import SocialNetworkIcon from '@/components/ui-components/SocialNetworkIcon';
import Spacer from '@/components/ui-components/Spacer';
import useMoneyFormat from '@/hooks/useMoneyFormat';
import LoadingSpinner from '@/components/ui-components/LoadingSpinner';
import Typography from '../../components/Typography';

const useStyles = makeStyles((theme) => ({
  root: {},
  mapContainer: {
    position: 'relative',
    height: '90%',
    backgroundColor: theme.palette.primary.extralight,
    borderBottomRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
    },
  },
  mapAddressOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 500,
    // transform: "translate3d(0,0,0)",
    backgroundColor: theme.palette.greyScale.superdark,
    color: theme.palette.greyScale.superlight,
    opacity: 0.8,
    minHeight: '32px',
    [theme.breakpoints.up('md')]: {
      borderTopLeftRadius: '6px',
      borderTopRightRadius: '6px',
    },
    padding: theme.spacing(1),
  },
  dataText: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1.5),
  },
  socialNetworkIconLink: {
    marginRight: theme.spacing(1),
    borderRadius: '24px',
    height: '24px',
    width: '24px',
    textAlign: 'center',
    paddingTop: theme.spacing(0.25),
  },
  websiteLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
}));

interface Props {
  data: CompanyInfoType
  currency: string
}

const defaultProps = {};

const CompanyMap = dynamic(import('./CompanyMap'), {
  ssr: false,
  loading: () => <LoadingSpinner />,
});

const CompanyInformation = ({
  data, currency,
}: Props) => {
  const classes = useStyles();
  const position = [data.location?.latitude, data.location?.longitude] as [number, number];
  const theme = useTheme();
  const { t } = useTranslation('markets');

  React.useEffect(() => {
    // https://github.com/PaulLeCam/react-leaflet/issues/453#issuecomment-541142178
    // without this hack, leaflet won't load the marker

    // eslint-disable-next-line global-require
    const L = require('leaflet');

    // eslint-disable-next-line no-underscore-dangle
    delete L.Icon.Default.prototype._getIconUrl;

    L.Icon.Default.mergeOptions({
      // eslint-disable-next-line global-require
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      // eslint-disable-next-line global-require
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      // eslint-disable-next-line global-require
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
  }, []);
  return (
    <div>
      {/* HEADER + DESCRIPTION */}
      <Typography variant="header">
        {t('markets.detailView.companyInformation.title')}
      </Typography>
      <Spacer spacing={2} />
      <Typography variant="base2">{data.description}</Typography>
      <Spacer spacing={3} />
      {/* CONTENT + LOCATION */}
      <Grid container direction="row" justify="center" alignItems="stretch">
        {/* CONTENT */}
        <Grid item xs={12} sm={7}>
          <Typography variant="base2">
            {t('markets.detailView.companyInformation.legalName')}
          </Typography>
          <Spacer spacing={1} />
          <Typography variant="subheader" className={classes.dataText}>
            {data.legal_name}
          </Typography>
          <Grid
            container
            direction="row"
            justify="center"
            alignItems="flex-start"
            alignContent="center"
          >
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t('markets.detailView.companyInformation.website')}
              </Typography>
              <Spacer spacing={1} />
              <Typography
                variant="subheader"
                noWrap
                className={classes.dataText}
              >
                {data.website ? (
                  <a
                    target="_blank"
                    href={data.website}
                    className={classes.websiteLink}
                    rel="noreferrer"
                  >
                    {data.website.split(`//`).pop()}
                  </a>
                ) : (
                  '-'
                )}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t('markets.detailView.companyInformation.socialMedia')}
              </Typography>
              {/* social media icons */}
              <Spacer spacing={1} />
              {data.social_media?.length > 0 ? (
                <Box display="flex" flexDirection="row">
                  {data.social_media?.map((socialItem) => (
                    <>
                      {(socialItem.url && socialItem.url !== "")
                    && (
                    <a
                      target="_blank"
                      key={socialItem.network}
                      href={socialItem.url}
                      className={classes.socialNetworkIconLink}
                      rel="noreferrer"
                    >
                      <SocialNetworkIcon
                        color={theme.palette.primary.main}
                        network={socialItem.network}
                      />
                    </a>
                    )}
                    </>
                  ))}
                </Box>
              ) : (
                <Typography variant="subheader">-</Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t('markets.detailView.companyInformation.founded')}
              </Typography>
              <Spacer spacing={1} />
              <Typography variant="subheader" className={classes.dataText}>
                {data.date_founded ? new Date(data.date_founded).toLocaleDateString() : "-"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t('markets.detailView.companyInformation.employees')}
              </Typography>
              <Spacer spacing={1} />
              <Typography variant="subheader" className={classes.dataText}>
                {Math.ceil(data.number_of_employees!)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t('markets.detailView.companyInformation.previousRounds')}
              </Typography>
              <Spacer spacing={1} />
              <Typography variant="subheader" className={classes.dataText}>
                {data.number_of_previous_rounds || '-'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Spacer spacing={2} />
              <Typography variant="base2">
                {t(
                  'markets.detailView.companyInformation.previouslyCrowdfunded',
                )}
              </Typography>
              <Spacer spacing={1} />
              <Typography variant="subheader" className={classes.dataText}>
                {useMoneyFormat(
                  data.previously_crowdfunded_amount,
                  'en-US',
                  currency,
                  'currency',
                  'symbol',
                  0,
                  2,
                )}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* LOCATION */}
        <Grid item xs={12} sm={5}>
          <Typography variant="base2">
            {t('markets.detailView.companyInformation.location')}
          </Typography>
          <Spacer spacing={1} />
          <div className={classes.mapContainer}>
            {(data.location.latitude && data.location.longitude)
            && <CompanyMap position={position} />}

            <div className={classes.mapAddressOverlay}>
              <Typography variant="small2medium" color="inherit">
                {data.location.address}
                ,
                {' '}
                {data.location.city}
                ,
                {' '}
                {data.location.country}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

CompanyInformation.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default CompanyInformation;
